import React from 'react';
import { parseCourseSchedule, formatTime, timeToMinutes, detectConflicts } from '../utils/calendarUtils';

/**
 * CalendarGrid Component
 * Displays weekly course schedule with time slots
 */
function CalendarGrid({ courses, onRemoveCourse }) {
  // Time configuration (9am - 9pm)
  const startHour = 9;
  const endHour = 21;
  const timeSlots = [];

  for (let hour = startHour; hour < endHour; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }

  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' }
  ];

  // Parse all course schedules and filter out courses without valid meeting times
  console.log('📅 CALENDAR GRID DEBUG: Rendering', courses.length, 'courses');

  const courseSchedules = courses
    .map(course => {
      const schedule = parseCourseSchedule(course);
      console.log(`  Course ${course.class_tag}:`, {
        days: course.days,
        start_times: course.start_times,
        schedule: schedule
      });
      return {
        course,
        schedule
      };
    })
    .filter(({ schedule }) => {
      // Only include courses that have at least one valid time slot
      const hasSchedule = Object.keys(schedule).length > 0;
      if (!hasSchedule) {
        console.log('  ⚠️ Filtered out course with no valid schedule');
      }
      return hasSchedule;
    });

  console.log('📅 CALENDAR GRID: After filtering,', courseSchedules.length, 'courses have valid schedules');

  // Get courses for specific day and time slot
  const getCoursesAtTime = (day, slotTime) => {
    const slotStart = timeToMinutes(slotTime);
    const slotEnd = slotStart + 60; // 1-hour slot

    return courseSchedules.filter(({ schedule }) => {
      const daySchedule = schedule[day];
      if (!daySchedule) return false;

      const courseStart = timeToMinutes(daySchedule.start);
      const courseEnd = timeToMinutes(daySchedule.end);

      // Check if course overlaps with this time slot
      return courseStart < slotEnd && courseEnd > slotStart;
    });
  };

  // Check if course has conflicts
  const getCourseConflicts = (course) => {
    const otherCourses = courses.filter(c => c.class_tag !== course.class_tag);
    return detectConflicts(course, otherCourses);
  };

  // Calculate position and height for course block
  const getCourseBlockStyle = (daySchedule, slotTime) => {
    const slotStart = timeToMinutes(slotTime);
    const courseStart = timeToMinutes(daySchedule.start);
    const courseEnd = timeToMinutes(daySchedule.end);

    // Position from top of slot (in pixels, 60px per hour)
    const offsetMinutes = Math.max(0, courseStart - slotStart);
    const top = (offsetMinutes / 60) * 60;

    // Height based on duration
    const durationMinutes = courseEnd - courseStart;
    const height = (durationMinutes / 60) * 60;

    return {
      top: `${top}px`,
      height: `${Math.max(height, 30)}px` // Minimum 30px height
    };
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-dark-100 dark:via-dark-200 dark:to-dark-100 min-h-screen">
      {/* Calendar Header */}
      <div className="bg-white dark:bg-dark-200 border-b border-gray-200 dark:border-dark-400 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-800 tracking-tight">
              Course Calendar
            </h1>
            <p className="text-sm text-gray-600 dark:text-dark-600 mt-1">
              Spring 2026 • {courses.length} course{courses.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-600 dark:text-dark-600">
              {courses.reduce((total, c) => total + (parseInt(c.units) || 4), 0)} units
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div>
        <div className="bg-white dark:bg-dark-200 rounded-xl shadow-sm border border-gray-200 dark:border-dark-400 overflow-hidden">
          <div className="grid grid-cols-6 border-b border-gray-200 dark:border-dark-400">
            {/* Time column header */}
            <div className="bg-gray-50 dark:bg-dark-300 px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
              Eastern
            </div>

            {/* Day headers */}
            {days.map(day => (
              <div
                key={day.key}
                className="bg-gray-50 dark:bg-dark-300 px-4 py-3 text-xs font-medium text-gray-700 dark:text-dark-700 uppercase tracking-wider text-center"
              >
                {day.label}
              </div>
            ))}
          </div>

          {/* Time slots */}
          <div className="divide-y divide-gray-100 dark:divide-dark-400">
            {timeSlots.map(time => (
              <div key={time} className="grid grid-cols-6 h-15">
                {/* Time label */}
                <div className="px-4 py-2 text-xs text-gray-600 dark:text-dark-600 border-r border-gray-100 dark:border-dark-400">
                  {formatTime(time)}
                </div>

                {/* Day cells */}
                {days.map(day => {
                  const coursesHere = getCoursesAtTime(day.key, time);

                  return (
                    <div
                      key={`${day.key}-${time}`}
                      className="relative border-r border-gray-100 dark:border-dark-400 min-h-[60px]"
                    >
                      {coursesHere.map(({ course, schedule }) => {
                        const daySchedule = schedule[day.key];
                        if (!daySchedule) return null;

                        // Only render once per course per day (at its start time)
                        const courseStartSlot = timeSlots.find(slot => {
                          const slotMinutes = timeToMinutes(slot);
                          const courseMinutes = timeToMinutes(daySchedule.start);
                          return slotMinutes <= courseMinutes && courseMinutes < slotMinutes + 60;
                        });

                        if (courseStartSlot !== time) return null;

                        const blockStyle = getCourseBlockStyle(daySchedule, time);
                        const conflicts = getCourseConflicts(course);
                        const hasConflict = conflicts.length > 0;

                        return (
                          <div
                            key={course.class_tag}
                            className={`absolute left-1 right-1 rounded-lg p-2 cursor-pointer group transition-all ${
                              hasConflict
                                ? 'bg-red-500/90 border-2 border-red-600'
                                : 'bg-accent-primary/90 border-2 border-accent-primary'
                            }`}
                            style={blockStyle}
                            onClick={() => onRemoveCourse && onRemoveCourse(course)}
                            title={hasConflict ? `Conflicts with ${conflicts[0].course.class_tag}` : 'Click to remove'}
                          >
                            <div className="text-white">
                              <div className="font-semibold text-xs leading-tight">
                                {course.class_tag}
                              </div>
                              <div className="text-xs opacity-90 mt-0.5 line-clamp-2">
                                {course.class_name}
                              </div>
                              <div className="text-xs opacity-75 mt-1">
                                {formatTime(daySchedule.start)} - {formatTime(daySchedule.end)}
                              </div>
                            </div>

                            {/* Remove button on hover */}
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveCourse && onRemoveCourse(course);
                                }}
                                className="w-5 h-5 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center"
                              >
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>

                            {hasConflict && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white" title="Schedule conflict" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Empty state or courses with missing times */}
        {courses.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-dark-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-dark-800">No courses added</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-600">
              Search for courses and click "Add to Calendar" to build your schedule
            </p>
          </div>
        )}

        {courses.length > 0 && courseSchedules.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-yellow-400 dark:text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-dark-800">Courses added but times not available</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-600">
              You have {courses.length} course{courses.length !== 1 ? 's' : ''} selected, but meeting times are TBA. Check my.harvard for updated schedules.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendarGrid;